import { useEffect, useSyncExternalStore } from 'react'
import { useAuth } from '../contexts/AuthContext'
import {
    getRentalOwnerAccessSnapshot,
    loadRentalOwnerAccess,
    subscribeRentalOwnerAccess,
    type RentalOwnerAccessStatus,
} from './rentalOwnerAccessStore'

/**
 * Se o utilizador deve ver as páginas do proprietário em Arrendamento (Contratos, Receitas,
 * Recebimentos): é proprietário activo de uma unidade OU tem histórico de arrendamentos como
 * proprietário. Um morador que é só inquilino não as vê — não tem nada para gerir ali.
 *
 * A resposta é a do servidor (`GET /api/arrendamentos/proprietario/acesso`), partilhada por toda a
 * app (Sidebar, CondominiumLayout, índice do módulo) — um só pedido por utilizador + condomínio.
 * Estados:
 * - `loading`  → ainda sem resposta (o menu esconde-se, a rota espera).
 * - `visible` / `hidden` → resposta do servidor.
 * - `error`    → o pedido falhou: o menu esconde-se, mas a rota NÃO é bloqueada (o servidor continua a
 *                ser a autoridade — a um não-proprietário só devolve listas vazias).
 */
export function useRentalOwnerAccess(): { status: RentalOwnerAccessStatus; canSeeOwnerPages: boolean } {
    const { user, hasPermission } = useAuth()
    const current = useSyncExternalStore(
        subscribeRentalOwnerAccess, getRentalOwnerAccessSnapshot, getRentalOwnerAccessSnapshot)

    // Sem entidade no condomínio, ou sem a permissão do módulo, não há nada a perguntar ao servidor.
    const applicable = !!user?.temEntidade && !!user.estateId && hasPermission('ARRENDAMENTO_GERIR_PROPRIO')
    const key = applicable ? `${user.id}:${user.estateId}` : null

    useEffect(() => {
        if (key) loadRentalOwnerAccess(key)
    }, [key])

    const status: RentalOwnerAccessStatus = !applicable ? 'hidden' : current.key === key ? current.status : 'loading'
    return { status, canSeeOwnerPages: status === 'visible' }
}
